import { getAccessPolicy } from "@features/access";
import { useAuthStore } from "@features/authentication/stores/authStore";
import { useLayoutContext } from "@router/layouts";
import { PageLoading } from "@shared/components/PageLoading";
import useCurrentUser from "@shared/queries/useCurrentUser";
import clsx from "clsx";
import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLikedYouUsers } from "../queries/useLikedYouUsers";

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/);
  if (parts.length >= 2) {
    return `${parts[0]?.[0] ?? ""}${parts[1]?.[0] ?? ""}`.toUpperCase();
  }
  return displayName.slice(0, 2).toUpperCase() || "?";
}

export default function LikedYouPage() {
  const { userId } = useAuthStore();
  const navigate = useNavigate();
  const { setHeader } = useLayoutContext();
  const { user: currentUser, isLoading: isCurrentUserLoading } = useCurrentUser();
  const { data: likers, isLoading: isLikersLoading, error, refetch } = useLikedYouUsers(userId ?? "");

  useEffect(() => {
    setHeader({
      variant: "back",
      title: "Liked you",
      onBack: () => navigate("/discover"),
    });
    return () => setHeader(null);
  }, [setHeader, navigate]);

  if (!userId) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-gray-600">Sign in to see who liked you.</p>
      </div>
    );
  }

  if (isLikersLoading || isCurrentUserLoading) {
    return <PageLoading message="Loading…" />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 p-8">
        <h2 className="mb-2 font-mono text-xl font-semibold text-gray-900">Something went wrong</h2>
        <p className="text-gray-600 mb-6">{error.message}</p>
        <button
          type="button"
          onClick={() => refetch()}
          className="bg-pink-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-pink-700 transition-colors"
          data-testid="liked-you-retry"
        >
          Try again
        </button>
      </div>
    );
  }

  const locked = Boolean(currentUser && !getAccessPolicy(currentUser).canSeeLikedYou);

  if (!likers?.length) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[50vh] p-8 text-center">
        <h2 className="mb-2 font-mono text-xl font-semibold text-gray-900">No likes yet</h2>
        <p className="text-gray-600 max-w-sm">
          {locked
            ? "When someone likes you first they appear here — VIP unlocks this list during the event."
            : "When someone likes you first, they will show up here."}
        </p>
      </div>
    );
  }

  return (
    <div className="p-4 pb-8">
      {locked ? (
        <p className="mb-3 text-center font-mono text-[length:var(--font-size-body-small)] font-semibold text-lavender-800">
          They liked you first — unlock to swipe them in Discover.
        </p>
      ) : null}
      <ul
        className={clsx(
          "grid grid-cols-3 sm:grid-cols-4 gap-4 max-w-lg mx-auto",
          locked && "pointer-events-none select-none opacity-[0.92]"
        )}
        data-testid="liked-you-grid"
      >
        {likers.map((user) => (
          <li key={user.uid} className="flex flex-col items-center gap-2">
            <button
              type="button"
              onClick={() => {
                if (!locked) {
                  navigate("/discover", { state: { startWithUserId: user.uid } });
                }
              }}
              disabled={locked}
              className={clsx(
                "size-20 sm:size-24 rounded-full overflow-hidden border-2 border-white shadow-md",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-pink-500 focus-visible:ring-offset-2",
                !locked && "transition-transform hover:scale-105 active:scale-95",
                locked && "cursor-default"
              )}
              data-testid={`liked-you-avatar-${user.uid}`}
              aria-label={
                locked ? `${user.display_name} — unlock to open in Discover` : `Open ${user.display_name} in discover`
              }
            >
              {user.photo_url ? (
                <img src={user.photo_url} alt="" className="size-full object-cover" />
              ) : (
                <span className="flex size-full items-center justify-center bg-pink-200 text-pink-900 text-lg font-semibold">
                  {initials(user.display_name)}
                </span>
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
